import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Star,
  Loader2,
  CheckCircle2,
  Image as ImageIcon,
  X,
  PenSquare,
  Sparkles,
  Crop as CropIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PhotoCropDialog } from "./PhotoCropDialog";

interface Props {
  onSubmitted: () => void;
  scrollIntoViewKey?: number;
}

const MAX_PHOTO_BYTES = 2 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

const RATING_LABELS: Record<number, string> = {
  1: "Disappointing",
  2: "Could be better",
  3: "Solid",
  4: "Really good",
  5: "Excellent",
};

export function ReviewSubmissionCard({ onSubmitted, scrollIntoViewKey }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const cardRef = useRef<HTMLDivElement>(null);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [text, setText] = useState("");
  const [name, setName] = useState(
    (user?.user_metadata?.full_name as string) || (user?.user_metadata?.name as string) || ""
  );
  const [role, setRole] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (scrollIntoViewKey !== undefined && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [scrollIntoViewKey]);

  const handlePhoto = (file: File | null) => {
    if (!file) {
      setPhotoFile(null);
      setPhotoPreview(null);
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({
        title: "Unsupported image format",
        description: "Please upload a JPG, PNG, or WEBP file.",
        variant: "destructive",
      });
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast({
        title: "Image too large",
        description: "Maximum size is 2MB.",
        variant: "destructive",
      });
      return;
    }
    // Open crop dialog instead of saving directly
    const reader = new FileReader();
    reader.onload = (e) => setCropSrc(String(e.target?.result || ""));
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = (croppedFile: File) => {
    setPhotoFile(croppedFile);
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(String(e.target?.result || ""));
    reader.readAsDataURL(croppedFile);
    setCropSrc(null);
  };

  const fileToDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const textLen = text.trim().length;
  const nameLen = name.trim().length;
  const canSubmit = rating >= 1 && textLen >= 30 && nameLen >= 2 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const photo_data_url = photoFile ? await fileToDataUrl(photoFile) : null;
      const { data, error } = await supabase.functions.invoke("submit-review", {
        body: {
          rating,
          review_text: text.trim(),
          display_name: name.trim(),
          display_role: role.trim() || null,
          photo_data_url,
        },
      });

      if (error) {
        const msg = (error as any)?.context?.error || error.message || "Failed to submit review";
        toast({ title: "Could not submit review", description: msg, variant: "destructive" });
        setSubmitting(false);
        return;
      }
      if (data?.error) {
        toast({ title: "Could not submit review", description: data.error, variant: "destructive" });
        setSubmitting(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => onSubmitted(), 5000);
    } catch (e: any) {
      toast({
        title: "Could not submit review",
        description: e?.message || "Unexpected error",
        variant: "destructive",
      });
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        ref={cardRef}
        className="rounded-2xl border border-success/30 bg-gradient-to-br from-success/10 via-success/5 to-transparent p-8 sm:p-10 text-center space-y-4"
      >
        <div className="mx-auto h-14 w-14 rounded-2xl bg-success/15 flex items-center justify-center">
          <CheckCircle2 className="w-7 h-7 text-success" />
        </div>
        <div>
          <h3 className="text-xl font-semibold">Thank you for your review!</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mt-1.5">
            <span className="font-semibold text-foreground">15 credits</span> have been added to
            your account. We appreciate you taking the time to share your experience.
          </p>
        </div>
      </div>
    );
  }

  // Live progress for the submit button
  const completedSteps = [rating >= 1, textLen >= 30, nameLen >= 2].filter(Boolean).length;

  return (
    <div
      ref={cardRef}
      className="rounded-2xl border border-border/60 bg-card overflow-hidden shadow-fluent-2"
    >
      {/* Header */}
      <div className="p-5 sm:p-6 border-b border-border/60 bg-muted/20 flex items-center gap-3">
        <div className="app-icon-box">
          <PenSquare className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold leading-tight">Write your review</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Reviews are public after a quick moderation check.
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
          <span className="font-semibold text-foreground">{completedSteps}</span>
          <span>/ 3</span>
        </div>
      </div>

      <div className="p-5 sm:p-6 space-y-6">
        {/* Rating */}
        <div>
          <div className="flex items-end justify-between mb-2 gap-2">
            <Label className="text-sm">
              How would you rate Email2Contact? <span className="text-destructive">*</span>
            </Label>
            {(hoverRating || rating) > 0 && (
              <span className="text-xs font-medium text-warning">
                {RATING_LABELS[hoverRating || rating]}
              </span>
            )}
          </div>
          <div
            className="inline-flex items-center gap-0.5 p-2 rounded-xl bg-muted/40 border border-border/60"
            onMouseLeave={() => setHoverRating(0)}
          >
            {[1, 2, 3, 4, 5].map((n) => {
              const filled = (hoverRating || rating) >= n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  className="p-1.5 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                >
                  <Star
                    className={cn(
                      "w-7 h-7 transition-colors",
                      filled ? "fill-warning text-warning" : "text-muted-foreground/30"
                    )}
                  />
                </button>
              );
            })}
          </div>
        </div>

        {/* Review text */}
        <div>
          <div className="flex items-end justify-between mb-2 gap-2">
            <Label htmlFor="review-text" className="text-sm">
              Tell us about your experience <span className="text-destructive">*</span>
            </Label>
            <span
              className={cn(
                "text-xs tabular-nums",
                textLen < 30
                  ? "text-muted-foreground"
                  : textLen >= 1000
                  ? "text-destructive"
                  : "text-success"
              )}
            >
              {textLen} / 1000 (min 30)
            </span>
          </div>
          <Textarea
            id="review-text"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 1000))}
            placeholder="What do you like? How does Email2Contact help you save time?"
            rows={5}
            maxLength={1000}
          />
        </div>

        {/* Name & Role */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="review-name" className="text-sm mb-2 block">
              Display name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="review-name"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 50))}
              placeholder="Jane Doe"
              maxLength={50}
            />
          </div>
          <div>
            <Label htmlFor="review-role" className="text-sm mb-2 block">
              Role / Title{" "}
              <span className="text-xs text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="review-role"
              value={role}
              onChange={(e) => setRole(e.target.value.slice(0, 80))}
              placeholder="Sales Manager"
              maxLength={80}
            />
          </div>
        </div>

        {/* Photo */}
        <div>
          <Label className="text-sm mb-2 block">
            Profile photo{" "}
            <span className="text-xs text-muted-foreground font-normal">
              (optional · JPG/PNG/WEBP · max 2MB)
            </span>
          </Label>
          {photoPreview ? (
            <div className="flex items-center gap-3 p-3 rounded-lg border border-border/60 bg-muted/20">
              <img
                src={photoPreview}
                alt="preview"
                className="w-14 h-14 rounded-full object-cover border-2 border-background"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">Photo cropped & ready</p>
                <p className="text-xs text-muted-foreground">
                  Tap "Re-crop" to adjust framing
                </p>
              </div>
              <label className="inline-flex">
                <Button type="button" variant="outline" size="sm" asChild>
                  <span className="cursor-pointer">
                    <CropIcon className="w-4 h-4 mr-1" /> Re-crop
                  </span>
                </Button>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => handlePhoto(e.target.files?.[0] || null)}
                />
              </label>
              <Button type="button" variant="ghost" size="sm" onClick={() => handlePhoto(null)}>
                <X className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          ) : (
            <label className="flex items-center gap-3 cursor-pointer text-sm border-2 border-dashed border-border/60 rounded-lg p-4 hover:bg-muted/30 hover:border-primary/40 transition-colors">
              <div className="h-10 w-10 rounded-lg bg-muted/40 flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <span className="font-medium">Choose photo</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  You'll be able to crop and zoom before saving
                </p>
              </div>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => handlePhoto(e.target.files?.[0] || null)}
              />
            </label>
          )}
        </div>

        {/* Submit footer */}
        <div className="pt-2 border-t border-border/60 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            15 credits added immediately on submit
          </p>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            size="lg"
            className="min-w-[200px]"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…
              </>
            ) : (
              "Submit review · +15 credits"
            )}
          </Button>
        </div>
      </div>

      <PhotoCropDialog
        open={!!cropSrc}
        imageSrc={cropSrc}
        onCancel={() => setCropSrc(null)}
        onConfirm={handleCropConfirm}
      />
    </div>
  );
}
